import React from 'react';
import posthogJs from 'posthog-js';
import { APP_BUILD_PROPERTIES } from '@/services/buildInfo';
import { captureSsoHandoffCode, scrubSsoHandoffFromEvent } from '@/utils/ssoHandoff';

// Web variant of PostHogProvider. posthog-react-native's session replay is
// native-only (Platform.select({ web: undefined })), so the web build uses
// posthog-js directly — it is the only PostHog SDK that can record web
// session replays. The exported `posthog` object is API-compatible with the
// subset used in services/analytics.ts (register, capture, identify, alias,
// reset).

const apiKey = process.env.EXPO_PUBLIC_POSTHOG_KEY;
const host = process.env.EXPO_PUBLIC_POSTHOG_HOST || 'https://eu.i.posthog.com';

export let posthog: typeof posthogJs | null = null;

// Take the landing SSO handoff code out of the address bar before posthog-js
// reads the URL for the initial $pageview / session replay (SEC-34). Runs even
// without a PostHog key so Sentry and history never see it either.
captureSsoHandoffCode();

if (apiKey && typeof window !== 'undefined') {
  posthogJs.init(apiKey, {
    api_host: host,
    autocapture: false,
    capture_pageview: 'history_change',
    // Frontend exception autocapture was off, so unanticipated JS/DOM errors
    // (outside the React ErrorBoundary's manual Sentry.captureException) were
    // invisible in PostHog. `capture_exceptions` wires up posthog-js's global
    // window.onerror / unhandledrejection listeners automatically.
    capture_exceptions: true,
    // Replay privacy: all input values are masked. React Native Web can't
    // set CSS classes directly, so sensitive containers (chat, analysis
    // reports) opt out of recording via dataSet={{ phNoCapture: 'true' }},
    // which renders as data-ph-no-capture and matches blockSelector.
    session_recording: {
      maskAllInputs: true,
      blockSelector: '[data-ph-no-capture]',
    },
    persistence: 'localStorage+cookie',
    // Belt and braces for the pre-init strip above: never send an SSO code in
    // any URL property.
    before_send: (event) => scrubSsoHandoffFromEvent(event),
    // Build identity on every event (incl. the initial $pageview and
    // $exception), not just after identify, so errors can be tied to a deploy.
    // `loaded` runs inside init, before the initial pageview is captured.
    loaded: (client) => {
      client.register(APP_BUILD_PROPERTIES);
    },
  });
  posthog = posthogJs;
}

export function PostHogProvider({ children }: { children: React.ReactNode }) {
  return <>{children}</>;
}
